import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker, Form, Input, Select, Button } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import deleteIcon from "../../../../Assets/Images/delete-icon.svg";
import backArrow from "../../../../Assets/Images/back-arrow.svg";
import { toast } from "react-toastify";
import moment from "moment";
import { FundraiserServices } from "../../../../Services/API/fundraisersService";
import ImageHandler from "../../../../Utils/ImageHandler";
import { dateUtils } from "../../../../Utils/DateFormatter";
import { userServices } from "../../../../Services/API/userServices";
import { TeamService } from "../../../../Services/API/TeamsService";
import { sessionServices } from "../../../../Utils/SessionServices";
import ProductsModal from "./ProductsModal";
import Loader from "../../../../Components/Loader/Loader";
import { idExtractor } from "../../../../Utils/Split";
import dayjs from "dayjs";
import OverlayLoaderModal from "../../../../Components/OverlayLoader/OverlayLoader";

export default function FundraisersForm() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const action = location?.state?.action;
  const state = location?.state?.data;
  // THE BELOW TWO ATTRIBUTES WILL BE SENT ONLY WHEN NAVIGATING FROM THE FUNDRAISER DASHBOARD PAGE
  const history = location?.state?.history;
  const campaignId = location?.state?.campaignId;
  const formRegex = /^[^<>{}()]*$/;

  // FETCHING AND STORING THE ACCESS TOKEN AND THE ROLE FROM THE SESSION STORAGE USING SESSIONSERVICES
  // STARTS HERE
  const [accessToken, setAccessToken] = useState("");
  const [role, setRole] = useState("");

  const [selectedGsaEmail, setSelectedGsaEmail] = useState("");
  const [selectedfhEmail, setSelectedfhEmail] = useState("");

  // STATE TO ENABLE AND DISABLE FORM FIELDS BASED ON LOGGED IN USER ROLE AND ACTION(ADD)
  const [formItemState, setFormItemState] = useState(true);

  // STATE TO SHOW AND HIDE THE LOADER OVERLAY WHILE IMAGE IS BEING UPLOADED TO SHOPIFY
  const [OverlayState, setOverlayState] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    sessionServices
      ?.getSessionStorageItem("token")
      .then((res) => {
        setAccessToken(res);
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    sessionServices
      ?.getSessionStorageItem("role")
      .then((res) => {
        setRole(res);
      })
      .catch((err) => console.log(err));
  }, []);

  // BASED ON THE ROLE OF THE LOGGED IN USER AND THE ACTION THEY ARE TRYING TO PERFORM, WE SHALL DISABLE OR ENABLE FORM ITEMS
  // FOR A FH, HE/SHE SHOULD NOT BE ABLE TO CHANGE ANYTHING IN A FUNDRAISER EXCEPT IMAGES, STORY AND SHORT DESC.
  // FOR A GSA, HE/SHE CAN CREATE A FUNDRAISER OR THEIR OWN, BUT THEY CAN EDIT ONLY THE IMAGES, STORY, SHORT DESC
  // AND SELECTED PRODUCTS
  useEffect(() => {
    if (role === "HERO") {
      setFormItemState(true);
    } else {
      setFormItemState(false);
    }
  }, [role, action]);

  // ENDS HERE

  // PRODUCTS HANDLING
  const [productsList, setProductsList] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [openProductsModal, setOpenProductsModal] = useState(false);

  const getAllProducts = () => {
    FundraiserServices.getAllProducts()
      .then((res) => {
        // ADDDING A CHECKED PROP TO EACH OF THE PRODUCTS OBJECT TO MAINTAIN SELECTED STATUS
        // FALSE BY DEFAULT
        let temp = res?.data?.map((item) => {
          return { ...item, checked: true };
        });
        setProductsList(temp);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    getAllProducts();
  }, []);

  const handleProductsChange = (productArray) => {
    // THE PRODUCTARRAY WILL HAVE ALL THE PRODUCTS,BUT ONLY THE SELECTED PRODUCTS IN THAT WILL BE CHECKED AS TRUE
    // HENCE WE FILTER IT BASED ON THAT AND SET THE FILTERED ARRAY BACK TO THE SELECTEDPRODUCTS ARRAY STATE
    // WHICH WILL BE RENDERED IN THE FORM PAGE, DOWN BELOW
    let temp = productArray?.filter((item) => item?.checked);
    setSelectedProducts(temp);
  };
  // ENDS HERE

  // IF DATA PRESENT IN THE STATE, IE.,EDIT FLOW, DATA IS TO BE SET IN THE FORM FIELDS
  // STARTS HERE
  useEffect(() => {
    // IF STATE HAS DATA, SET THE VALUES TO THE FORM
    if (state) {
      form.setFieldsValue({
        title: state?.title,
        start_date: dayjs(state?.start_date, "YYYY-MM-DD"),
        end_date: dayjs(state?.end_date, "YYYY-MM-DD"),
        location: state?.location,
        club: state?.club,
        goal: state?.goal,
        bannerImg: "",
        teamImg: "",
        carouselImg: "",
        shortDsc: state?.short_description,
        story: state?.description,
      });

      // PRODUCTS AND IMAGES ARE HANDLED USING STATE, THE SELECTED PRODUCTS ARRAY FROM STATE
      // IS SET TO SELECTEDPRODUCTS STATE ARRAY, WHICH WILL BE USED TO RENDER THE SELECTED PRODUCTS BELOW
      setSelectedProducts(state?.products);

      // WE GET THE IMAGE GIDs IN THE STATE, SO WE SET THEM TO THEIR RESPECTIVE STATES.
      // IF THERE IS A CHANGE IN ANY OF THE IMAGES, THAT NEW IMAGE GID WILL BE SET TO THE STATE
      // ELSE THE OLD GIDs WILL BE FORWARDED, HENCE WE SET THE GIDs TO THE STATE
      setBannerImageId(state?.campaign_image_id);
      setExistingBannerImageUrl(state?.imageUrl);
      setTeamImageId(state?.team_image_id);
      setExistingTeamImageUrl(state?.team_imageUrl);
      setCarouselImageId(state?.carousel_image_id);
      setExistingCarouselImageUrl(state?.carousel_imageUrl);
      setSelectedGsaEmail(state?.gsa_string);
      setSelectedfhEmail(state?.fundraiser_hero_string);

      // SETTING THE START DATE INTO THE STATE, FOR DISABLING THE CORRECT DATES
      // IN END DATE DATEPICKER
      setStartDate(
        state?.start_date ? dayjs(state?.start_date, "YYYY-MM-DD") : ""
      );

      // MANIPULATE THE TEAM GID AND EXTRACT THE ID FROM IT AND SET IT TO SPLITTEAMID STATE
      // IF NO CHANGE IN THE TEAM, THIS WILL BE PASSED ON TO THE PAYLOAD
      // IF CHANGED, THAT SCENARIO IS HANDLED IN THE TEAMS FORM ITEM ONCHANGE FUNCTION
      // TEAMS DATA IS UNSTABLE AT THE MOMENT, HENCE THE LENGTH CHECK
      if (state?.teams?.length > 0) {
        let temp = idExtractor(state?.teams[0]?.id, "/");
        setSplitTeamId(temp[temp?.length - 1]);
      }
    }
  }, [state]);

  // ENDS HERE

  // FETCHING AND STORING THE DROPDOWN VALUES FOR GSA, TEAMS AND FUNDRAISER HEROES
  // STARTS HERE
  const [loader, setLoader] = useState(true);
  const [gsaList, setGsaList] = useState([]);
  const [selectedGsa, setSelectedGsa] = useState("");
  const [teamList, setTeamList] = useState([]);
  const [splitTeamId, setSplitTeamId] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [fundraiserHeroList, setFundraiserHeroList] = useState([]);
  const [selectedFundraiserHero, setSelectedFundraiserHero] = useState("");
  const [dropDownDisable, setDropDownDisable] = useState(false);

  const getDropdownVals = async () => {
    try {
      // FETCHING GSA LIST
      let gsaListArray = [];
      if (role === "ADMIN" || role === "HERO") {
        const adminResponse = await userServices.getUserBasedOnRole(
          accessToken,
          "GSA"
        );
        adminResponse?.data?.forEach((gsa) => {
          let temp = {
            label: `${gsa?.firstName} ${gsa?.lastName} - ${gsa?.email}`,
            value: gsa?.id,
            requiredPayloadName: `${gsa?.firstName} ${gsa?.lastName} | ${gsa?.email}`,
          };
          gsaListArray.push(temp);
          return;
        });

        if (role === "HERO") {
          setDropDownDisable(true);
        }
      } else if (role === "GSA") {
        setDropDownDisable(true);
        const nonAdminResponse = await userServices.getCurrentUserFromShopify(
          accessToken
        );
        gsaListArray = nonAdminResponse?.data?.data?.metaobjects?.nodes?.map(
          (item) => ({
            label: `${item?.firstName?.value} ${item?.lastName?.value} - ${item?.email?.value}`,
            value: item?.id,
            requiredPayloadName: `${item?.firstName?.value} ${item?.lastName?.value} | ${item?.email?.value} `,
          })
        );
        setSelectedGsaEmail(
          nonAdminResponse?.data?.data?.metaobjects?.nodes[0]?.email?.value
        );
      }
      setGsaList(gsaListArray);

      // FETCHING FH LIST
      const allHeroList = await userServices.getUserBasedOnRole(
        accessToken,
        "HERO"
      );
      if (allHeroList) {
        let tempHeroList = [];
        allHeroList?.data?.forEach((item) => {
          let temp = {
            label: `${item?.firstName} ${item?.lastName} - ${item?.email}`,
            value: item?.id,
            requiredPayloadName: `${item?.firstName} ${item?.lastName} | ${item?.email}`,
          };
          tempHeroList?.push(temp);
        });
        setFundraiserHeroList(tempHeroList);
      }

      // FETCHING TEAMS LIST
      const allTeamsList = await TeamService?.getAllTeams();
      if (allTeamsList) {
        let tempTeamList = [];
        allTeamsList?.data?.forEach((item) => {
          let temp = {
            label: `${item?.team_title}`,
            value: `${item?.id}`,
          };
          tempTeamList?.push(temp);
        });
        setTeamList(tempTeamList);
      }

      // ONCE ALL THE DROPDOWN VALS ARE FETCHED, THEN SET THE ININIAL VALUES

      if (action === "add") {
        form.setFieldsValue({
          GSA: role === "GSA" ? gsaListArray[0]?.value : "",
        });
      } else {
        form.setFieldsValue({
          GSA: state?.gsa,
          hero: state?.fundraiser_hero,
          team: state?.teams[0]?.id,
        });
      }
      setLoader(false);
    } catch (error) {
      console.log("err", error);
      toast.error("Something went wrong, try again later");
      navigate("/");
    }
  };

  useEffect(() => {
    try {
      setLoader(true);
      // CHECKING IF THE ROLE VALUE IS PRESENT, TO MAKE SURE WE POPULATE THE CORRECT VALUES IN THE DROPDOWNS
      if (role) {
        getDropdownVals();
      }
    } catch (error) {
      setLoader(false);
    }
  }, [role]);

  // ENDS HERE

  // IMAGE HANDLING
  // HANDLING THE IMAGE FILE, IMAGE META DATA, IMAGE ID, AND ITS RESPECTIVE BUTTON STATE

  const [carouselImage, setCarouselImage] = useState();
  const [carouselImageInfo, setCarouselImageInfo] = useState({});
  const [carouselImageId, setCarouselImageId] = useState("");
  const [existingCarouselImageUrl, setExistingCarouselImageUrl] = useState("");
  const [carouselBtndisabled, setCarouselBtndisabled] = useState(false);

  const [teamImage, setTeamImage] = useState();
  const [teamImageInfo, setTeamImageInfo] = useState({});
  const [teamImageId, setTeamImageId] = useState("");
  const [existingTeamImageUrl, setExistingTeamImageUrl] = useState("");
  const [teamBtndisabled, setTeamBtndisabled] = useState(false);

  const [bannerImage, setBannerImage] = useState();
  const [bannerImageInfo, setBannerImageInfo] = useState({});
  const [bannerImageId, setBannerImageId] = useState("");
  const [existingBannerImageUrl, setExistingBannerImageUrl] = useState("");
  const [bannerBtndisabled, setBannerBtndisabled] = useState(false);

  // IMAGE HANDLER IS A PROMISE THAT WILL TAKE IN THE IMAGE WHEN UPLOADED AS AN EVENT AND  IT DOES THE FOLLOWING THINGS
  // 1. CHECK IF THE SIZE OF THE IMAGE IS LESS THAN 10MB, IF NOT, REJECTS THE PROMISE AND POSTS AN ERROR TOAST.
  // 2. IF YES, IT WILL RETRIEVE THE FILE NAME, SIZE AND TYPE AND RETURNS AN OBJECT

  const changeImageHandler = (event, imageType) => {
    ImageHandler(event).then(([item, file]) => {
      switch (imageType) {
        case "banner":
          // IN THE SCENARIO WHEN THE USER DOES NOT DELETE AN EXISTING BANNER IMAGE (EDIT FLOW)
          // AND TRIES TO SAVE IT WITHOUT UPLOADING THE NEW IMAGE, AND DELETES IT, BOTH, OLD AND THE
          // NEW IMAGE MUST BE DELETED. HENCE CLEARING THE EXISTING BANNER IMAGE URL AND THE
          // OLD IMAGE GID WHICH IS SET IN THE BANNERIMAGEID STATE.
          setBannerImageId();
          setExistingBannerImageUrl();
          setBannerImage(file);
          setBannerImageInfo(item);
          break;
        case "team":
          setTeamImage(file);
          setTeamImageInfo(item);
          break;
        case "carousel":
          setCarouselImage(file);
          setCarouselImageInfo(item);
          break;
        default:
          break;
      }
    });
  };

  const uploadImageHandler = (imageType) => {
    setOverlayState(true);
    let formData = new FormData();
    switch (imageType) {
      case "banner":
        formData.append("filename", bannerImageInfo?.name);
        formData.append("fileSize", bannerImageInfo?.size);
        formData.append("mimeType", bannerImageInfo?.type);
        formData.append("file", bannerImage);
        break;
      case "team":
        formData.append("filename", teamImageInfo?.name);
        formData.append("fileSize", teamImageInfo?.size);
        formData.append("mimeType", teamImageInfo?.type);
        formData.append("file", teamImage);
        break;
      case "carousel":
        formData.append("filename", carouselImageInfo?.name);
        formData.append("fileSize", carouselImageInfo?.size);
        formData.append("mimeType", carouselImageInfo?.type);
        formData.append("file", carouselImage);
        break;
      default:
        break;
    }

    FundraiserServices.addMedia(formData)
      .then((res) => {
        if (imageType === "banner") {
          setBannerImageId(res?.data[0]?.id);
          setBannerBtndisabled(true);
        } else if (imageType === "team") {
          setTeamImageId(res?.data[0]?.id);
          setTeamBtndisabled(true);
        } else if (imageType === "carousel") {
          setCarouselImageId(res?.data[0]?.id);
          setCarouselBtndisabled(true);
        }
        setOverlayState(false);
        toast.success(`${imageType} image uploaded successfully`);
      })
          .catch((err) => {
      const backendMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        JSON.stringify(err?.response?.data) ||
        "Something went wrong";

      toast.error(backendMessage);
      setOverlayState(false);
      console.log(err);
});
  };

  // IMAGE HANDLER FUNCTIONS END HERE

  // HANDLING THE CAMPAIGN/FUNDRAISER FORM SAVE

  const renderToast = () => {
    const missingItems = [];

    if (!bannerImageId) {
      missingItems.push("banner Image");
    }
    if (!selectedProducts || selectedProducts.length === 0) {
      missingItems.push("Products");
    }

    if (missingItems.length > 0) {
      missingItems.forEach((item) => {
        if (item === "banner Image") {
          toast.error(`Select and upload ${item}`);
        } else {
          toast.error(`${item} is not present`);
        }
      });
    }
  };

  const handleCreateFundraiser = (values) => {
    // Utility function to escape special characters
    const escapeSpecialChars = (str) => {
      return str.replace(/\\/g, "\\\\").replace(/\"/g, '\\"');
    };

    // Escape special characters in story and short description
    const escapedStory = escapeSpecialChars(values?.story);
    const escapedShortDsc = escapeSpecialChars(values?.shortDsc);

    // MODIFY THE SELECTED PRODUCTS ARRAY FOR BACKEND REQUIREMENTS
    let tempProductsArray = selectedProducts.map((product) => product.id);
    let finalProductsArray = tempProductsArray.map((item) => {
      let idArray = idExtractor(item, "/");
      return idArray[idArray.length - 1];
    });
    const gsa_string = gsaList?.find(
      (item) => item?.value === values?.GSA
    )?.requiredPayloadName;
    const fundraiser_hero_string = fundraiserHeroList?.find(
      (item) => item?.value === values?.hero
    )?.requiredPayloadName;

    let tempObj = {
      title: values?.title,
      short_description: escapedShortDsc,
      start_date: dateUtils?.createDate(values?.start_date?.$d),
      end_date: dateUtils?.createDate(values?.end_date?.$d),
      goal: values?.goal,
      team_image: teamImageId ? teamImageId : "",
      carousel_image: carouselImageId ? carouselImageId : "",
      description: escapedStory,
      location: values?.location,
      fundraiser_hero: values?.hero,
      fundraiser_hero_string: fundraiser_hero_string,
      gsa: values?.GSA,
      gsa_string: gsa_string,
      club: values?.club,
      team: [splitTeamId],
    };
    if (action === "add") {
      // CHECK IF THE GIDs OF ALL THE THREE IMAGES ARE OBTAINED, ELSE, THROW ERROR TOAST
      if (bannerImageId && finalProductsArray?.length > 0) {
        tempObj["raised"] = 0;
        tempObj["total_raised"] = 0;
        tempObj["donors"] = 0;
        tempObj["image"] = bannerImageId;
        tempObj["products"] = finalProductsArray;

        FundraiserServices?.addFundraiser(tempObj)
          .then((res) => {
            toast.success("Campaign added successfully");
            navigate("/");
          })
          .catch((err) => {
            console.log(err);
            toast.error("Something went wrong");
          });
      } else {
        renderToast();
      }
    } else if (action === "edit") {
      // CHECKING IF THE USER HAS DELETED THE EXISTING BANNER IMAGE OR NOT
      if (bannerImageId && finalProductsArray?.length > 0) {
        tempObj["raised"] = state?.raised;
        tempObj["total_raised"] = state?.total_raised;
        tempObj["donors"] = state?.donors;
        tempObj["image"] = bannerImageId;
        tempObj["products"] = finalProductsArray;
        tempObj["created_by"] = state?.created_by;

        FundraiserServices?.updateFundraiser(state?.id, tempObj)
          .then((res) => {
            toast.success(
              "Updated, Please wait for a few minutes for the changes to reflect"
            );
            navigate("/");
          })
          .catch((err) => {
            console.log(err);
            toast.error("Something went wrong");
          });
      } else {
        renderToast();
      }
    }
  };

  // HANDLING DATE VALIDATIONS

  const handleStartDateChange = (date) => {
    console.log(date);
    setStartDate(date);
    if (endDate && date && date.isAfter(endDate)) {
      toast.error("Start date must be before end date");
      setStartDate(null);
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (startDate && date && date.isBefore(startDate)) {
      toast.error("End date must be after start date");
      setEndDate(null);
    }
  };

  if (loader) {
    return <Loader />;
  } else {
    return (
      <div className="form-page">
        <div className="forms-header-component">
          <div className="back-btn-container">
            <img
              src={backArrow}
              className="back-arrow"
              onClick={() => {
                if (history === "dash") {
                  navigate(`/fundraiser/${campaignId}`);
                } else {
                  navigate("/");
                }
              }}
              alt="Back to Fundraisers"
            />
          </div>
          <div className="heading-container">
            {action === "add" ? "Add New Fundraiser" : state?.title}
          </div>
        </div>
        <div className="fundraiser-form-component">
          <div className="form-container">
            <Form
              form={form}
              className="form-component"
              labelCol={{
                span: 8,
              }}
              wrapperCol={{
                span: 16,
              }}
              colon={false}
              onFinish={handleCreateFundraiser}
            >
              <Form.Item
                className="form-item"
                label={<div className="form-label">Fundraiser Name:</div>}
                name="title"
                rules={[
                  {
                    required: true,
                    message: "Fundraiser Name is required",
                  },
                  {
                    pattern: formRegex,
                    message: "Enter valid Fundraiser name",
                  },
                ]}
              >
                <Input disabled={formItemState} />
              </Form.Item>
              <Form.Item
                className="form-item"
                label={<div className="form-label">GSA:</div>}
                name="GSA"
                rules={[
                  {
                    required: true,
                    message: "GSA is required",
                  },
                ]}
              >
                <Select
                  value={selectedGsa}
                  options={gsaList}
                  disabled={dropDownDisable}
                  onChange={(e, option) => {
                    setSelectedGsa(e);
                    setSelectedGsaEmail(option?.label?.split(" - ")?.[1]);
                  }}
                ></Select>
              </Form.Item>
              <Form.Item
                className="form-item"
                label={<div className="form-label">Location:</div>}
                name="location"
                rules={[
                  {
                    required: true,
                    message: "Location is required",
                  },
                  {
                    pattern: formRegex,
                    message: "Enter a valid Location",
                  },
                ]}
              >
                <Input disabled={formItemState} />
              </Form.Item>
              <Form.Item
                className="form-item"
                label={<div className="form-label">Fundraiser Hero:</div>}
                name="hero"
                rules={[
                  {
                    required: true,
                    message: "Fundraiser Hero is required",
                  },
                ]}
              >
                <Select
                  value={selectedFundraiserHero}
                  onChange={(e, option) => {
                    setSelectedfhEmail(option?.label?.split(" - ")?.[1]);
                    setSelectedFundraiserHero(e);
                  }}
                  options={fundraiserHeroList}
                  disabled={formItemState}
                ></Select>
              </Form.Item>
              <Form.Item
                className="form-item"
                label={<div className="form-label">Team:</div>}
                name="team"
                rules={[
                  {
                    required: true,
                    message: "Team is required",
                  },
                ]}
              >
                <Select
                  value={selectedTeam}
                  onChange={(e) => {
                    setSelectedTeam(e);
                    // WE HAVE TO PASS JUST THE ID FROM THE GID OF THE TEAM, NOT THE BASE URL IN IT
                    // HENCE, WE ARE SPLITTING THE GID BASED IN '/' AND EXTRACTING THE ID FROM IT
                    // AND SETTING IT TO SPLITTEAMID STATE, WHICH WE PASS ON IN THE PAYLOAD
                    let temp = idExtractor(e, "/");
                    setSplitTeamId(temp[temp?.length - 1]);
                  }}
                  options={teamList}
                  disabled={formItemState}
                ></Select>
              </Form.Item>
              <Form.Item
                className="form-item"
                label={<div className="form-label">Club/Organisation:</div>}
                name="club"
                rules={[
                  {
                    required: true,
                    message: "Club is required",
                  },
                  {
                    pattern: formRegex,
                    message: "Enter a valid Club name",
                  },
                ]}
              >
                <Input disabled={formItemState} />
              </Form.Item>
              <Form.Item
                className="form-item"
                label={<div className="form-label">Start Date:</div>}
                name="start_date"
                rules={[
                  {
                    required: true,
                    message: "Start Date is required",
                  },
                ]}
              >
                <DatePicker
                  format="YYYY-MM-DD"
                  defaultValue={
                    state?.start_date
                      ? dayjs(state?.start_date, "YYYY-MM-DD")
                      : ""
                  }
                  onChange={handleStartDateChange}
                  disabledDate={(current) => {
                    return current && current < moment().startOf("day");
                  }}
                  disabled={formItemState}
                />
              </Form.Item>
              <Form.Item
                className="form-item"
                label={<div className="form-label">End Date:</div>}
                name="end_date"
                rules={[
                  {
                    required: true,
                    message: "End Date is required",
                  },
                ]}
              >
                <DatePicker
                  format="YYYY-MM-DD"
                  defaultValue={
                    state?.end_date ? dayjs(state?.end_date, "YYYY-MM-DD") : ""
                  }
                  onChange={handleEndDateChange}
                  disabledDate={(current) => {
                    if (startDate) {
                      return (
                        startDate && current && current.isBefore(startDate)
                      );
                    } else {
                      return true;
                    }
                  }}
                  disabled={formItemState}
                />
              </Form.Item>
              <Form.Item
                className="form-item"
                label={<div className="form-label">Goal:</div>}
                name="goal"
                rules={[
                  {
                    required: true,
                    message: "Goal is required",
                  },
                  {
                    pattern: /^[0-9]+$/,
                    message: "Enter valid amount",
                  },
                ]}
              >
                <Input
                  placeholder="Enter only numbers, example, 5000"
                  disabled={formItemState}
                />
              </Form.Item>

              {/* Images form items */}
              <Form.Item
                className="form-item image-form-item"
                label={
                  <div className="form-label">
                    <span className="asterisk">*</span>Banner Image:
                    <div className="helper-text">
                      This image is shown in the fundraisers page.
                    </div>
                  </div>
                }
                name="bannerImg"
              >
                {bannerImage ? (
                  <div className="img-actions-container">
                    <div className="image-clearbtn-container">
                      <div className="clear-btn">
                        <img
                          className="deleteIcon"
                          src={deleteIcon}
                          onClick={() => {
                            // CLEARING THE BANNERIMAGE (IMAGE FILE),BANNERIMAGEINFO (IMAGE METADATA)
                            // BANNERIMAGEID (ADD FLOW WILL BE EMPTY, EDIT FLOW WILL HAVE EXISTING IMAGE GID),
                            // AND EXISTINGBANNERIMAGEURL
                            setBannerImage();
                            setBannerImageInfo({});
                            setBannerImageId("");
                            setExistingBannerImageUrl("");
                            setBannerBtndisabled(false);
                          }}
                        />
                      </div>
                      <img
                        className="uploaded-img"
                        src={
                          bannerImage ? URL.createObjectURL(bannerImage) : null
                        }
                        alt="banner"
                      />
                    </div>
                    <div className="image-actions-btns-container">
                      <div className="instructions-text">
                        <ExclamationCircleOutlined className="exclamation" />
                        Click on upload, after selecting an image
                      </div>
                      <div className="btns-container">
                        <label className="fileUploadBtn">
                          Browse
                          <input
                            className="upload-input-form-item"
                            onChange={(event) =>
                              changeImageHandler(event, "banner")
                            }
                            type="file"
                          />
                        </label>
                        <Button
                          className="browse-btn"
                          disabled={bannerBtndisabled}
                          onClick={() => uploadImageHandler("banner")}
                        >
                          Upload
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {existingBannerImageUrl ? (
                      <div className="img-actions-container">
                        <div className="image-clearbtn-container">
                          <div className="clear-btn">
                            <img
                              className="deleteIcon"
                              src={deleteIcon}
                              onClick={() => {
                                setExistingBannerImageUrl("");
                                setBannerImageId("");
                              }}
                            />
                          </div>
                          <img
                            className="uploaded-img"
                            src={existingBannerImageUrl}
                            alt="banner"
                          />
                        </div>
                        <div className="image-actions-btns-container">
                          <div className="btns-container">
                            <label className="fileUploadBtn">
                              Browse
                              <input
                                className="upload-input-form-item"
                                onChange={(event) =>
                                  changeImageHandler(event, "banner")
                                }
                                type="file"
                              />
                            </label>
                            <Button
                              className="browse-btn"
                              disabled={true}
                              onClick={() => uploadImageHandler("banner")}
                            >
                              Upload
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="img-actions-container">
                        <div className="empty-img-box"></div>
                        <div className="image-actions-btns-container">
                          <div className="instructions-text">
                            <ExclamationCircleOutlined className="exclamation" />
                            Click on upload, after selecting an image
                          </div>
                          <div className="btns-container">
                            <label className="fileUploadBtn">
                              Browse
                              <input
                                className="upload-input-form-item"
                                onChange={(event) =>
                                  changeImageHandler(event, "banner")
                                }
                                type="file"
                              />
                            </label>
                            <Button disabled className="browse-btn">
                              Upload
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Form.Item>
              <Form.Item
                className="form-item image-form-item"
                label={
                  <div className="form-label">
                    Team Image:
                    <div className="helper-text">
                      This image is shown in the fundraisers page
                    </div>
                  </div>
                }
                name="teamImg"
              >
                {teamImage ? (
                  <div className="img-actions-container">
                    <div className="image-clearbtn-container">
                      <div className="clear-btn">
                        <img
                          className="deleteIcon"
                          src={deleteIcon}
                          onClick={() => {
                            setTeamImage();
                            setTeamImageInfo({});
                            setTeamImageId("");
                            setExistingTeamImageUrl("");
                            setTeamBtndisabled(false);
                          }}
                        />
                      </div>
                      <img
                        className="uploaded-img"
                        src={teamImage ? URL.createObjectURL(teamImage) : null}
                        alt="carousel"
                      />
                    </div>
                    <div className="image-actions-btns-container">
                      <div className="instructions-text">
                        <ExclamationCircleOutlined className="exclamation" />
                        Click on upload, after selecting an image
                      </div>
                      <div className="btns-container">
                        <label className="fileUploadBtn">
                          Browse
                          <input
                            className="upload-input-form-item"
                            onChange={(event) =>
                              changeImageHandler(event, "team")
                            }
                            type="file"
                          />
                        </label>
                        <Button
                          className="browse-btn"
                          disabled={teamBtndisabled}
                          onClick={() => uploadImageHandler("team")}
                        >
                          Upload
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {existingTeamImageUrl ? (
                      <div className="img-actions-container">
                        <div className="image-clearbtn-container">
                          <div className="clear-btn">
                            <img
                              className="deleteIcon"
                              src={deleteIcon}
                              onClick={() => {
                                setExistingTeamImageUrl("");
                                setTeamImageId("");
                              }}
                            />
                          </div>
                          <img
                            className="uploaded-img"
                            src={existingTeamImageUrl}
                            alt="carousel"
                          />
                        </div>
                        <div className="image-actions-btns-container">
                          <div className="btns-container">
                            <label className="fileUploadBtn">
                              Browse
                              <input
                                className="upload-input-form-item"
                                onChange={(event) =>
                                  changeImageHandler(event, "team")
                                }
                                type="file"
                              />
                            </label>
                            <Button
                              className="browse-btn"
                              disabled={true}
                              onClick={() => uploadImageHandler("team")}
                            >
                              Upload
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="img-actions-container">
                        <div className="empty-img-box"></div>
                        <div className="image-actions-btns-container">
                          <div className="instructions-text">
                            <ExclamationCircleOutlined className="exclamation" />
                            Click on upload, after selecting an image
                          </div>
                          <div className="btns-container">
                            <label className="fileUploadBtn">
                              Browse
                              <input
                                className="upload-input-form-item"
                                onChange={(event) =>
                                  changeImageHandler(event, "team")
                                }
                                type="file"
                              />
                            </label>
                            <Button disabled className="browse-btn">
                              Upload
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Form.Item>
              <Form.Item
                className="form-item image-form-item"
                label={
                  <div className="form-label">
                    Carousel Image:
                    <div className="helper-text">
                      This image is shown in the home page carousel card
                    </div>
                  </div>
                }
                name="carouselImg"
              >
                {carouselImage ? (
                  <div className="img-actions-container">
                    <div className="image-clearbtn-container">
                      <div className="clear-btn">
                        <img
                          className="deleteIcon"
                          src={deleteIcon}
                          onClick={() => {
                            setCarouselImage();
                            setCarouselImageInfo({});
                            setCarouselImageId("");
                            setExistingCarouselImageUrl("");
                            setCarouselBtndisabled(false);
                          }}
                        />
                      </div>
                      <img
                        className="uploaded-img"
                        src={
                          carouselImage
                            ? URL.createObjectURL(carouselImage)
                            : null
                        }
                        alt="carousel"
                      />
                    </div>
                    <div className="image-actions-btns-container">
                      <div className="instructions-text">
                        <ExclamationCircleOutlined className="exclamation" />
                        Click on upload, after selecting an image
                      </div>
                      <div className="btns-container">
                        <label className="fileUploadBtn">
                          Browse
                          <input
                            className="upload-input-form-item"
                            onChange={(event) =>
                              changeImageHandler(event, "carousel")
                            }
                            type="file"
                          />
                        </label>
                        <Button
                          className="browse-btn"
                          disabled={carouselBtndisabled}
                          onClick={() => uploadImageHandler("carousel")}
                        >
                          Upload
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {existingCarouselImageUrl ? (
                      <div className="img-actions-container">
                        <div className="image-clearbtn-container">
                          <div className="clear-btn">
                            <img
                              className="deleteIcon"
                              src={deleteIcon}
                              onClick={() => {
                                setExistingCarouselImageUrl("");
                                setCarouselImageId("");
                              }}
                            />
                          </div>
                          <img
                            className="uploaded-img"
                            src={existingCarouselImageUrl}
                            alt="carousel"
                          />
                        </div>
                        <div className="image-actions-btns-container">
                          <div className="btns-container">
                            <label className="fileUploadBtn">
                              Browse
                              <input
                                className="upload-input-form-item"
                                onChange={(event) =>
                                  changeImageHandler(event, "carousel")
                                }
                                type="file"
                              />
                            </label>
                            <Button
                              className="browse-btn"
                              disabled={true}
                              onClick={() => uploadImageHandler("carousel")}
                            >
                              Upload
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="img-actions-container">
                        <div className="empty-img-box"></div>
                        <div className="image-actions-btns-container">
                          <div className="instructions-text">
                            <ExclamationCircleOutlined className="exclamation" />
                            Click on upload, after selecting an image
                          </div>
                          <div className="btns-container">
                            <label className="fileUploadBtn">
                              Browse
                              <input
                                className="upload-input-form-item"
                                onChange={(event) =>
                                  changeImageHandler(event, "carousel")
                                }
                                type="file"
                              />
                            </label>
                            <Button disabled className="browse-btn">
                              Upload
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Form.Item>
              {/* Image form items end here */}

              <Form.Item
                className="form-item"
                label={<div className="form-label">Short Description:</div>}
                name="shortDsc"
                rules={[
                  {
                    required: true,
                    message: "Short Description is required",
                  },
                ]}
              >
                <Input.TextArea rows={5} />
              </Form.Item>
              <Form.Item
                className="form-item"
                label={<div className="form-label">Story:</div>}
                name="story"
                rules={[
                  {
                    required: true,
                    message: "Story is required",
                  },
                ]}
              >
                <Input.TextArea rows={10} />
              </Form.Item>
              <Form.Item
                className="form-item"
                label={
                  <div className="form-label">
                    <span className="asterisk">*</span>Fundraiser Products:
                  </div>
                }
                name="products"
              >
                <div className="selected-products">
                  {selectedProducts?.map((item) => {
                    return (
                      <div className="form-product-card">
                        <div className="image-container">
                          <img
                            className="form-product-image"
                            src={item?.images ? item?.images[0] : item?.image}
                            alt="selected product"
                          ></img>
                        </div>
                        <div className="details-container">
                          <p className="details">{item.title}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button
                  className="browse-products-btn"
                  onClick={() => setOpenProductsModal(true)}
                  disabled={role === "HERO" ? true : false}
                >{`${selectedProducts?.length > 0
                  ? `Edit Products`
                  : `Add Products`
                  }`}</Button>
                {openProductsModal && (
                  <ProductsModal
                    open={openProductsModal}
                    closeModal={() => setOpenProductsModal(false)}
                    allProducts={productsList}
                    selectedProducts={selectedProducts}
                    productsCrud={handleProductsChange}
                  />
                )}
              </Form.Item>
            </Form>
          </div>
        </div>
        <div className="forms-btns-container">
          <Button className="save-btn" onClick={() => form.submit()}>
            Save
          </Button>
          <Button className="cancel-btn" onClick={() => navigate("/")}>
            Cancel
          </Button>
        </div>
        {OverlayState && <OverlayLoaderModal isOpen={OverlayState} />}
      </div>
    );
  }
}
